import { useCallback } from 'react'

import { BillingPriceInterval } from '@tribeplatform/gql-client/types'
import { useQueryParam } from '@tribeplatform/react-components/common'
import { T } from '@tribeplatform/react-components/i18n'
import { useBillingProducts } from '@tribeplatform/react-sdk/hooks'
import { Card } from '@tribeplatform/react-ui-kit/Card'

import { SegmentProvider } from '../../../../providers/SegmentProvider.js'
import { useCurrentProductInformation } from '../hooks/useCurrentProductInformation.js'

import { BillingIntervalToggle } from './BillingIntervalToggle.js'
import { UpgradePlanCard } from './UpgradePlanCard.js'

const UpgradePlanCardLoading = () => {
  return (
    <>
      {[...Array(4)].map((_e, i) => (
        <Card
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className="h-full"
        >
          <Card.Content>
            <div className="animate-pulse">
              <div className="flex flex-col space-y-5 h-full">
                <div className="h-8 bg-skeleton rounded w-1/4" />
                <div className="h-4 bg-skeleton rounded-full" />
                <div className="h-4 bg-skeleton rounded-full w-2/3" />
                <div className="h-8 bg-skeleton rounded w-1/4" />
                <div className="h-4 bg-skeleton rounded-full w-1/3" />
                <div className="h-8 bg-skeleton rounded" />
                <div className="space-y-8">
                  <div className="h-4 bg-skeleton rounded w-1/4" />
                  <div className="h-4 bg-skeleton rounded w-1/4" />
                  <div className="h-4 bg-skeleton rounded w-1/3" />
                  <div className="h-4 bg-skeleton rounded w-1/4" />
                  <div className="h-4 bg-skeleton rounded w-1/3" />
                  <div className="h-4 bg-skeleton rounded w-1/4" />
                  <div className="h-4 bg-skeleton rounded w-1/3" />
                  <div className="h-4 bg-skeleton rounded w-1/4" />
                </div>
              </div>
            </div>
          </Card.Content>
        </Card>
      ))}
    </>
  )
}

export const PlansPage = () => {
  const [intervalQueryParam, setIntervalQueryParam] =
    useQueryParam<BillingPriceInterval>(
      { name: 'interval' },
      {
        navigationMode: 'replace',
      },
    )
  const { data: products, isInitialLoading: productsLoading } =
    useBillingProducts()
  const { currentInterval, isLoading: intervalLoading } =
    useCurrentProductInformation()
  const plans = products?.plans?.filter(plan => plan.public)
  const isLoading = productsLoading || intervalLoading
  const billingInterval = isLoading
    ? null
    : (intervalQueryParam ?? currentInterval ?? BillingPriceInterval.Year)

  const onIntervalToggleChanged = useCallback(
    (interval: BillingPriceInterval) => {
      setIntervalQueryParam(interval)
    },
    [setIntervalQueryParam],
  )

  return (
    <SegmentProvider>
      <div className="space-y-6">
        <div className="flex flex-col items-center">
          <h1 className="text-heading-md font-bold text-content">
            <T defaultMessage="Plans" id="Admin.Billing.Plans" />
          </h1>
          <p className="text-md text-content-muted">
            <T
              defaultMessage="Choose a plan and upgrade when you’re ready to unlock more features."
              id="Admin.Billing.PlanDescription"
            />
          </p>
        </div>
        <div className="space-y-5">
          <div className="flex justify-center">
            <BillingIntervalToggle
              billingInterval={billingInterval}
              onChange={onIntervalToggleChanged}
            />
          </div>
          <div className="grid grid-cols-1 @2xl:grid-cols-2 @4xl:grid-cols-2 gap-5">
            {isLoading && <UpgradePlanCardLoading />}
            {plans?.map(product => (
              <UpgradePlanCard
                key={product.id}
                product={product}
                interval={billingInterval}
              />
            ))}
          </div>
        </div>
      </div>
    </SegmentProvider>
  )
}
